import { Clock, Calendar as CalendarIcon, Repeat } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { Appointment } from '../types';

interface AppointmentCardProps {
  appointment: Appointment;
  onClick: () => void;
}

export function AppointmentCard({ appointment, onClick }: AppointmentCardProps) {
  const { t, i18n } = useTranslation('calendar');

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'de-DE', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatTime = (timeStr: string) => {
    // Time is in format HH:MM:SS or HH:MM
    const [hours, minutes] = timeStr.split(':');
    const suffix = t('card.timeSuffix');
    return suffix ? `${hours}:${minutes} ${suffix}` : `${hours}:${minutes}`;
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left glass-effect rounded-[15px] p-4 hover:bg-white/10 transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white truncate">
            {appointment.title}
          </h3>

          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-white/60">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-4 h-4" />
              {formatDate(appointment.appointment_date)}
            </span>

            {!appointment.is_all_day && appointment.appointment_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatTime(appointment.appointment_time)}
              </span>
            )}

            {appointment.is_all_day && (
              <span className="text-white/40">{t('card.allDay')}</span>
            )}
          </div>

          {appointment.description && (
            <p className="text-sm text-white/50 mt-2 line-clamp-2">
              {appointment.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {appointment.recurrence_group_id && (
            <Repeat
              className="w-4 h-4 text-white/60"
              aria-label={t('card.recurring')}
            />
          )}
          <span className="px-2 py-1 rounded-[5px] text-xs font-medium whitespace-nowrap bg-pp-teal text-white border border-white/25">
            {t(`appointmentTypes.${appointment.appointment_type}`)}
          </span>
        </div>
      </div>

      {appointment.is_predefined && appointment.ssw_week && (
        <div className="mt-2 text-xs text-pp-orange">
          SSW {appointment.ssw_week}
        </div>
      )}
    </button>
  );
}
